/**
 * MARCA | 28/05/2026 | Cart bundle nudge web component.
 * Manages the inline bundle upsell accordion in the mini-cart. Fetches products
 * with metafield data via section rendering, renders a dark-themed scrollable
 * product list with Product/Inspired By columns, handles slot selection, and
 * submits the bundle to cart via /cart/add.js.
 * MARCA | 29/05/2026 | Added tabbed collection switching (up to 3 tabs).
 */
if (!customElements.get('cart-bundle-nudge')) {
  customElements.define(
    "cart-bundle-nudge",
    class CartBundleNudge extends HTMLElement {
      constructor() {
        super();
        this.productsByCollection = {};
        this.filteredProducts = [];
        this.selectedSlots = {};
        this.activeSlotIndex = null;
        this.activeTab = null;
        this.isOpen = true;
        this.isLoading = false;
      }

      connectedCallback() {
        this.collectionHandle = this.dataset.collectionHandle;
        this.collections = JSON.parse(this.dataset.collections || "[]");
        this.currentVariantId = this.dataset.currentVariantId;
        this.currentProductTitle = this.dataset.currentProductTitle;
        this.currentProductImage = this.dataset.currentProductImage;
        this.totalSlots = parseInt(this.dataset.slots, 10) || 2;

        this.dismissBtn = this.querySelector("[data-nudge-dismiss]");
        this.expandBtn = this.querySelector("[data-nudge-expand]");
        this.contentEl = this.querySelector("[data-nudge-content]");
        this.slotsContainer = this.querySelector("[data-nudge-slots]");
        this.dropdown = this.querySelector("[data-nudge-dropdown]");
        this.searchInput = this.querySelector("[data-nudge-search]");
        this.resultsContainer = this.querySelector("[data-nudge-results]");
        this.completeBtn = this.querySelector("[data-nudge-complete]");
        this.tabsContainer = this.querySelector("[data-nudge-tabs]");

        this.bindEvents();
        this.fetchProducts();
      }

      bindEvents() {
        this.dismissBtn?.addEventListener("click", () => this.collapse());
        this.expandBtn?.addEventListener("click", () => this.expand());

        this.slotsContainer
          ?.querySelectorAll("[data-slot-toggle]")
          .forEach((btn) => {
            btn.addEventListener("click", (e) => {
              const slot = e.target.closest("[data-slot-index]");
              if (slot) this.openDropdown(parseInt(slot.dataset.slotIndex, 10));
            });
          });

        this.slotsContainer
          ?.querySelectorAll("[data-slot-index]")
          .forEach((slot) => {
            slot.addEventListener("click", (e) => {
              if (e.target.closest("[data-slot-toggle]")) return;
              this.openDropdown(parseInt(slot.dataset.slotIndex, 10));
            });
          });

        if (this.searchInput) {
          this.searchInput.addEventListener(
            "input",
            this.debounce(() => this.handleSearch(), 250),
          );
        }

        /* MARCA | 29/05/2026 | Tab click handler — switches active collection in dropdown */
        if (this.tabsContainer) {
          this.tabsContainer.addEventListener("click", (e) => {
            const tab = e.target.closest("[data-nudge-tab]");
            if (tab) this.switchTab(tab.dataset.nudgeTab);
          });
        }

        this.completeBtn?.addEventListener("click", () => this.submitBundle());

        document.addEventListener("click", (e) => {
          if (this.activeSlotIndex !== null && !this.contains(e.target)) {
            this.closeDropdown();
          }
        });
      }

      /* --- Accordion collapse/expand --- */
      collapse() {
        this.isOpen = false;
        const height = this.contentEl.scrollHeight;
        this.contentEl.style.maxHeight = height + "px";
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.contentEl.style.maxHeight = "0";
            this.classList.add("cart-bundle-nudge--collapsed");
          });
        });
        this.closeDropdown();
      }

      expand() {
        this.isOpen = true;
        this.classList.remove("cart-bundle-nudge--collapsed");
        this.contentEl.style.maxHeight = "0";
        requestAnimationFrame(() => {
          this.contentEl.style.maxHeight = this.contentEl.scrollHeight + "px";
          setTimeout(() => {
            this.contentEl.style.maxHeight = "none";
          }, 350);
        });
      }

      /* MARCA | 29/05/2026 | Fetch products via section rendering — returns keyed by collection handle */
      async fetchProducts() {
        this.isLoading = true;

        try {
          const response = await fetch(`/?sections=cart-bundle-products`);
          if (!response.ok) throw new Error("Failed to fetch products");
          const sections = await response.json();
          const html = sections["cart-bundle-products"] || "";
          const doc = new DOMParser().parseFromString(html, "text/html");
          const jsonStr = (doc.body.textContent || "").trim();
          const data = JSON.parse(jsonStr);

          if (Array.isArray(data)) {
            this.productsByCollection[this.collectionHandle] = data;
          } else {
            Object.keys(data).forEach((handle) => {
              this.productsByCollection[handle] = data[handle] || [];
            });
          }

          this.activeTab = this.collectionHandle;
          this.filteredProducts = this.productsByCollection[this.activeTab] || [];
        } catch (err) {
          console.error("[CartBundleNudge] Fetch error:", err);
          this.productsByCollection = {};
          this.filteredProducts = [];
        } finally {
          this.isLoading = false;
        }
      }

      /* MARCA | 29/05/2026 | Switch active tab — updates UI and filters product list */
      switchTab(handle) {
        if (this.activeTab === handle) return;
        this.activeTab = handle;

        if (this.tabsContainer) {
          this.tabsContainer.querySelectorAll("[data-nudge-tab]").forEach((tab) => {
            tab.classList.toggle(
              "cart-bundle-nudge__tab--active",
              tab.dataset.nudgeTab === handle,
            );
          });
        }

        if (this.searchInput) this.searchInput.value = "";
        this.filteredProducts = this.productsByCollection[handle] || [];
        this.renderResults();
      }

      /* --- Dropdown --- */
      openDropdown(slotIndex) {
        /* MARCA | 28/05/2026 | Toggle behaviour — clicking the active slot again closes the dropdown */
        if (this.activeSlotIndex === slotIndex) {
          this.closeDropdown();
          return;
        }

        this.activeSlotIndex = slotIndex;
        this.dropdown.hidden = false;
        if (this.searchInput) this.searchInput.value = "";
        this.filteredProducts = this.productsByCollection[this.activeTab] || [];
        this.renderResults();

        this.slotsContainer
          .querySelectorAll("[data-slot-index]")
          .forEach((s) => {
            s.classList.toggle(
              "cart-bundle-nudge__slot--active",
              parseInt(s.dataset.slotIndex, 10) === slotIndex,
            );
          });
      }

      closeDropdown() {
        this.activeSlotIndex = null;
        this.dropdown.hidden = true;
        this.slotsContainer
          .querySelectorAll("[data-slot-index]")
          .forEach((s) => {
            s.classList.remove("cart-bundle-nudge__slot--active");
          });
      }

      /* MARCA | 29/05/2026 | Search filtering — searches across ALL collections when query is active */
      handleSearch() {
        const query = (this.searchInput?.value || "").toLowerCase().trim();
        if (query.length === 0) {
          this.filteredProducts = this.productsByCollection[this.activeTab] || [];

          if (this.tabsContainer) {
            this.tabsContainer.querySelectorAll("[data-nudge-tab]").forEach((tab) => {
              tab.classList.toggle(
                "cart-bundle-nudge__tab--active",
                tab.dataset.nudgeTab === this.activeTab,
              );
            });
          }
        } else {
          const allProducts = [];
          Object.values(this.productsByCollection).forEach((arr) => {
            arr.forEach((p) => {
              if (!allProducts.find((x) => x.id === p.id)) {
                allProducts.push(p);
              }
            });
          });

          this.filteredProducts = allProducts.filter(
            (p) =>
              p.title.toLowerCase().includes(query) ||
              (p.inspiredBy || "").toLowerCase().includes(query),
          );

          if (this.tabsContainer) {
            this.tabsContainer.querySelectorAll("[data-nudge-tab]").forEach((tab) => {
              tab.classList.remove("cart-bundle-nudge__tab--active");
            });
          }
        }
        this.renderResults();
      }

      /* MARCA | 28/05/2026 | Render product list rows — includes "Same Again?" shortcut and dark-themed Product/Inspired By columns */
      renderResults() {
        if (!this.resultsContainer) return;

        if (this.isLoading) {
          this.resultsContainer.innerHTML =
            '<div class="cart-bundle-nudge__loading"><span class="cart-bundle-nudge__spinner"></span></div>';
          return;
        }

        const alreadySelected = [];
        const fragment = document.createDocumentFragment();

        /* MARCA | 24/08/2026 | "Same Again?" row — entire row is clickable to quick-add the current cart product */
        const sameAgainRow = document.createElement("button");
        sameAgainRow.type = "button";
        sameAgainRow.className =
          "cart-bundle-nudge__result-row cart-bundle-nudge__result-row--same";
        sameAgainRow.setAttribute("aria-label", "Add same product again");
        sameAgainRow.innerHTML = `
          <span class="cart-bundle-nudge__result-section-label">SAME AGAIN?</span>
          <span class="cart-bundle-nudge__result-title">${this.escapeHtml(this.currentProductTitle)}</span>
          <span class="cart-bundle-nudge__result-add" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
          </span>
        `;
        sameAgainRow.addEventListener("click", () => {
          this.selectProduct({
            variantId: this.currentVariantId,
            title: this.currentProductTitle,
            image: this.currentProductImage,
            inspiredBy: "",
          });
        });
        fragment.appendChild(sameAgainRow);

        if (this.filteredProducts.length === 0) {
          const empty = document.createElement("div");
          empty.className = "cart-bundle-nudge__empty";
          empty.textContent = "No products found.";
          fragment.appendChild(empty);
        } else {
          this.filteredProducts.forEach((product) => {
            const isSelected = alreadySelected.includes(
              String(product.variantId),
            );
            /* MARCA | 24/08/2026 | Entire result row is the add control — plus icon is visual only */
            const row = document.createElement("button");
            row.type = "button";
            row.className = "cart-bundle-nudge__result-row";
            row.setAttribute("aria-label", `Add ${product.title}`);
            if (isSelected) {
              row.classList.add("cart-bundle-nudge__result-row--disabled");
              row.disabled = true;
            }

            row.innerHTML = `
              <span class="cart-bundle-nudge__result-title">${this.escapeHtml(product.title)}</span>
              <span class="cart-bundle-nudge__result-inspired">${this.escapeHtml(product.inspiredBy)}</span>
              <span class="cart-bundle-nudge__result-add" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              </span>
            `;

            if (!isSelected) {
              row.addEventListener("click", () => {
                this.selectProduct(product);
              });
            }

            fragment.appendChild(row);
          });
        }

        this.resultsContainer.innerHTML = "";
        this.resultsContainer.appendChild(fragment);
      }

      /* --- Selection --- */
      selectProduct(product) {
        if (this.activeSlotIndex === null) return;

        this.selectedSlots[this.activeSlotIndex] = {
          variantId: String(product.variantId),
          title: product.title,
          image: product.image || "",
          inspiredBy: product.inspiredBy || "",
        };

        this.updateSlotUI(this.activeSlotIndex);
        this.closeDropdown();
        this.updateCompleteButton();
      }

      /* MARCA | 28/05/2026 | Slot UI update — shows product image, title, and "Inspired by" subtitle */
      updateSlotUI(slotIndex) {
        const slot = this.slotsContainer.querySelector(
          `[data-slot-index="${slotIndex}"]`,
        );
        if (!slot) return;

        const data = this.selectedSlots[slotIndex];
        const imageContainer = slot.querySelector("[data-slot-image]");
        const labelEl = slot.querySelector("[data-slot-label]");
        const subtitleEl = slot.querySelector("[data-slot-subtitle]");

        if (data) {
          slot.classList.add("cart-bundle-nudge__slot--filled");
          if (data.image) {
            imageContainer.innerHTML = `<img src="${this.escapeHtml(data.image)}" alt="${this.escapeHtml(data.title)}" width="48" height="48" loading="lazy">`;
          }
          labelEl.textContent = data.title;
          subtitleEl.textContent = data.inspiredBy
            ? `Inspired by ${data.inspiredBy}`
            : "";
        } else {
          slot.classList.remove("cart-bundle-nudge__slot--filled");
          imageContainer.innerHTML =
            '<span class="cart-bundle-nudge__slot-placeholder" aria-hidden="true">+</span>';
          labelEl.textContent = `Choose Fragrance ${slotIndex}`;
          subtitleEl.textContent = "Please Select an Option";
        }
      }

      updateCompleteButton() {
        const filledCount = Object.keys(this.selectedSlots).length;
        this.completeBtn.disabled = filledCount < this.totalSlots;
      }

      /* --- Submit bundle to cart --- */
      async submitBundle() {
        const items = Object.values(this.selectedSlots).map((slot) => ({
          id: parseInt(slot.variantId, 10),
          quantity: 1,
        }));

        if (items.length === 0) return;

        this.completeBtn.disabled = true;
        this.completeBtn.classList.add("cart-bundle-nudge__complete--loading");

        try {
          const response = await fetch(
            window.Shopify?.routes?.root
              ? `${window.Shopify.routes.root}cart/add.js`
              : "/cart/add.js",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({ items }),
            },
          );

          const data = await response.json();

          if (data.status && data.status !== 200) {
            console.error(
              "[CartBundleNudge] Cart error:",
              data.description || data.message,
            );
            this.completeBtn.disabled = false;
            this.completeBtn.classList.remove(
              "cart-bundle-nudge__complete--loading",
            );
            return;
          }

          /* MARCA | 17/09/2026 | On the ad landing, refresh the cart bar instead of the store drawer. */
          /* MARCA | 18/09/2026 | Gate on theme.promoLanding so the store mini-cart path is unchanged. */
          if (window.theme && theme.promoLanding && typeof theme.applyPromoDiscount === 'function') {
            const discounted = await theme.applyPromoDiscount(true);
            if (!discounted && typeof theme.refreshPromoCartBar === 'function') {
              await theme.refreshPromoCartBar(null, true);
            }
          } else {
            const miniCart = document.querySelector("mini-cart");
            if (miniCart) {
              const sectionsResponse = await fetch(
                `${window.location.pathname}?sections=mini-cart,cart-icon-bubble,mobile-cart-icon-bubble`,
              );
              const sections = await sectionsResponse.json();
              miniCart.renderContents({ sections });
            } else {
              window.location.href = window.Shopify?.routes?.root
                ? `${window.Shopify.routes.root}cart`
                : "/cart";
            }
          }
        } catch (err) {
          console.error("[CartBundleNudge] Submit error:", err);
          this.completeBtn.disabled = false;
          this.completeBtn.classList.remove(
            "cart-bundle-nudge__complete--loading",
          );
        }
      }

      /* --- Utilities --- */
      escapeHtml(str) {
        const div = document.createElement("div");
        div.textContent = str || "";
        return div.innerHTML;
      }

      debounce(fn, wait) {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, args), wait);
        };
      }
    },
  );
}
